import { type Dictionary, de } from './de.js';

/** Alle Schlüssel des Wörterbuchs als Punktpfade zu Zeichenketten (`nav.heute`). */
type Leaves<T, Prefix extends string = ''> = {
  [K in keyof T & string]: T[K] extends string ? `${Prefix}${K}` : Leaves<T[K], `${Prefix}${K}.`>;
}[keyof T & string];

type ValueAt<T, Path extends string> = Path extends `${infer Head}.${infer Rest}`
  ? Head extends keyof T
    ? ValueAt<T[Head], Rest>
    : never
  : Path extends keyof T
    ? T[Path]
    : never;

type PlaceholderNames<S extends string> = S extends `${string}{${infer Name}}${infer Rest}`
  ? Name | PlaceholderNames<Rest>
  : never;

export type TextKey = Leaves<Dictionary>;
export type Param = string | number;

/** Parameter eines Schlüssels: fehlen Platzhalter im Text, gibt es keine; sonst ist ein Objekt Pflicht. */
type ParamArgs<K extends TextKey> = [PlaceholderNames<ValueAt<Dictionary, K> & string>] extends [
  never,
]
  ? []
  : [params: Record<PlaceholderNames<ValueAt<Dictionary, K> & string>, Param>];

function lookup(key: string): string {
  let node: unknown = de;
  for (const part of key.split('.')) {
    if (node === null || typeof node !== 'object' || !(part in node)) {
      throw new Error(`Text-Schlüssel unbekannt: ${key}`);
    }
    node = (node as Record<string, unknown>)[part];
  }
  if (typeof node !== 'string') {
    throw new Error(`Text-Schlüssel unbekannt: ${key} (kein Text)`);
  }
  return node;
}

/**
 * Ein Durchgang über die Vorlage: eingesetzte Werte werden nicht erneut durchsucht. Fehlt ein Parameter, wirft
 * die Funktion und nennt `what` (Schlüssel bzw. Vorlage) und den Namen.
 */
function substitute(template: string, params: Record<string, Param>, what: string): string {
  return template.replaceAll(/\{(\w+)\}/g, (_match, name: string) => {
    const value = params[name];
    if (value === undefined) {
      throw new Error(`Parameter fehlt für ${what}: ${name}`);
    }
    return String(value);
  });
}

/**
 * Text aus dem Wörterbuch mit eingesetzten Parametern. Ein unbekannter Schlüssel oder ein fehlender Parameter
 * ist zur Übersetzungszeit ein Typfehler und wirft zur Laufzeit.
 */
export function t<K extends TextKey>(key: K, ...args: ParamArgs<K>): string {
  return substitute(lookup(key), (args[0] ?? {}) as Record<string, Param>, key);
}

/** Parameter einer Vorlage: ohne Platzhalter keine, sonst ein Objekt mit genau diesen Namen. */
type FillArgs<S extends string> = [PlaceholderNames<S>] extends [never]
  ? []
  : [params: Record<PlaceholderNames<S>, Param>];

/**
 * Setzt die Platzhalter (`{name}`) eines **Seitentextes** ein, also einer Vorlage, die nicht im Wörterbuch der
 * Hülle steht, sondern in `texte.ts` der Seite (`as const`, damit der Wortlaut als Typ erhalten bleibt). Dieselbe
 * Prüfung wie bei `t()`: ein fehlender oder falsch benannter Parameter ist ein Typfehler, zur Laufzeit ein Wurf.
 */
export function fill<const S extends string>(template: S, ...args: FillArgs<S>): string {
  return substitute(template, (args[0] ?? {}) as Record<string, Param>, `„${template}“`);
}
